import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import {
  overview, syncHealth, listUsers, listAudit, createOrg, setOrgStatus, setOrgPlan,
  setPlatformAdmin, invitePlatformAdmin, setUserDisabled, setPlatformRole, platformRoleCatalog, findUser, orgTeam, setMemberRole, removeMember,
  setMemberDisabled, resendInvite,
  listPlans, upsertPlan, deletePlan, errText, ago, ZERO, SOURCE_LABEL,
  type Counts, type Member, type Role, type Plan, type MemberState, type PlatformRole, type PlatformRoleCatalog,
  type PlatformUser, type SyncRun, type AuditEntry, type Source,
} from './api';
import { Ico, ICON } from './icons';
import { IntegrationsPanel } from './integrations';
import { VendorsPanel } from './vendors';

// `manager` was missing here, so the console could show a manager and offer no
// way to make one. Order matches the tenant side, most privileged first.
const ROLES: Role[] = ['owner', 'admin', 'manager', 'tech', 'viewer'];

/**
 * Where somebody stands, as a pill.
 *
 * `invited` is the one that earns its place: an account that exists, counts
 * against the member total, and belongs to somebody who has never managed to
 * sign in. Folded into "active" it is invisible, which is exactly how people
 * end up stranded outside a tenant that believes it added them weeks ago.
 */
function StateTag({ state }: { state: MemberState }) {
  const tone = state === 'active' ? 'tag-ok' : state === 'disabled' ? 'tag-bad' : 'tag-warn';
  return <span className={'tag ' + tone}>{state}</span>;
}

/**
 * Two-factor, stated either way.
 *
 * Rendering a badge only when MFA is on makes "no MFA" and "we did not check"
 * look identical, and on the platform-admin list those are very different
 * facts. So the absence is labelled too.
 */
function MfaTag({ on }: { on: boolean }) {
  return on
    ? <span className="tag tag-ok">2FA</span>
    : <span className="tag" title="No second factor enrolled">no 2FA</span>;
}

/** Small helper so every view gets the same load/error/empty handling. */
function useLoad<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true); setError(null);
    try { setData(await fn()); }
    catch (e) { setError(errText(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { void reload(); }, deps);
  return { data, loading, error, reload };
}

function Shell({ loading, error, children }: { loading: boolean; error: string | null; children: ReactNode }) {
  if (loading) return <div className="empty">Loading…</div>;
  if (error) return <div className="banner banner-error">{error}</div>;
  return <>{children}</>;
}

/** Total plus each non-zero bucket. Zeroes are noise. */
export function CountPills({ c }: { c: Counts }) {
  const keys: (keyof Counts)[] = ['healthy', 'warning', 'failed', 'paused', 'unknown'];
  return (
    <span className="cnt-row">
      <span className="cnt cnt-total">{c.total}</span>
      {keys.filter((k) => c[k] > 0).map((k) => (
        <span key={k} className={'cnt cnt-' + k} title={k}>{c[k]}</span>
      ))}
    </span>
  );
}

// ---------------------------------------------------------------- Overview

export function OverviewView({ onOpen }: { onOpen: (orgId: string) => void }) {
  const { data, loading, error } = useLoad(() => overview({}));

  return (
    <Shell loading={loading} error={error}>
      {data && (
        <>
          <div className="stat-row">
            <Stat label="Organisations" value={data.orgCount} sub={`${data.activeOrgCount} active · ${data.suspendedOrgCount} suspended`} />
            <Stat label="Machines monitored" value={data.totals.total} sub="across active tenants" />
            <Stat label="Failed" value={data.totals.failed} tone="fail" sub="need attention now" />
            <Stat label="Warning" value={data.totals.warning} tone="warn" sub="degrading" />
            <Stat label="Unknown" value={data.totals.unknown} tone="dim" sub="no data reported" />
          </div>

          <section className="panel">
            <h3 className="panel-title">Tenants at a glance</h3>
            {data.orgs.length === 0 ? <div className="empty">No organisations yet.</div> : (
              <table className="mini-table">
                <thead><tr><th>Organisation</th><th>Status</th><th>Backups</th><th>Last sync</th><th /></tr></thead>
                <tbody>
                  {data.orgs.map((o) => (
                    <tr key={o.orgId}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{o.name}</div>
                        <div className="mono dim" style={{ fontSize: 11 }}>{o.slug}</div>
                      </td>
                      <td><StatusTag status={o.status} /></td>
                      <td><CountPills c={o.counts} /></td>
                      <td className="mono dim">{ago(o.lastSyncedAt)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn-link" onClick={() => onOpen(o.orgId)}>open</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </Shell>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: number; sub?: string; tone?: string }) {
  return (
    <div className={'stat' + (tone ? ' stat-' + tone : '')}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

function StatusTag({ status }: { status: string }) {
  return <span className={'tag ' + (status === 'active' ? 'tag-ok' : 'tag-bad')}>{status}</span>;
}

// ----------------------------------------------------------------- Tenants

export function TenantsView({ onOpen }: { onOpen: (orgId: string) => void }) {
  const { data, loading, error, reload } = useLoad(() => overview({}));
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function act(key: string, fn: () => Promise<unknown>) {
    setBusy(key); setErr(null);
    try { await fn(); await reload(); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(null); }
  }

  return (
    <Shell loading={loading} error={error}>
      {err && <div className="banner banner-error">{err}</div>}
      <NewOrg onCreate={(n, s) => act('new', () => createOrg({ name: n, slug: s }))} busy={busy === 'new'} />

      <section className="panel">
        <h3 className="panel-title">Organisations</h3>
        {!data || data.orgs.length === 0 ? <div className="empty">No organisations yet.</div> : (
          <table className="mini-table">
            <thead>
              <tr><th>Name</th><th>Org ID</th><th>Status</th><th>Backups</th><th>Members</th><th>Created</th><th /></tr>
            </thead>
            <tbody>
              {data.orgs.map((o) => (
                <tr key={o.orgId}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{o.name}</div>
                    <div className="mono dim" style={{ fontSize: 11 }}>{o.slug}</div>
                  </td>
                  <td className="mono dim">{o.orgId}</td>
                  <td><StatusTag status={o.status} /></td>
                  <td><CountPills c={o.counts} /></td>
                  <td className="mono">{o.memberCount}</td>
                  <td className="mono dim">{o.createdAt ? new Date(o.createdAt).toLocaleDateString() : '--'}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn-link" onClick={() => onOpen(o.orgId)}>open</button>
                    {' · '}
                    <button
                      className="btn-link" disabled={busy === o.orgId}
                      onClick={() => act(o.orgId, () => setOrgStatus({
                        orgId: o.orgId, status: o.status === 'active' ? 'suspended' : 'active',
                      }))}
                    >
                      {o.status === 'active' ? 'suspend' : 'activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="panel-note">
          Suspending is real, not cosmetic — the collectors only sweep active organisations, so a
          suspended tenant stops syncing on the next run.
        </p>
      </section>
    </Shell>
  );
}

function NewOrg({ onCreate, busy }: { onCreate: (n: string, s: string) => void; busy: boolean }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const auto = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 31);

  return (
    <section className="panel">
      <h3 className="panel-title">New organisation</h3>
      <form
        className="row-form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!name.trim() || !slug.trim()) return;
          onCreate(name.trim(), slug.trim());
          setName(''); setSlug('');
        }}
      >
        <label className="field">
          <span className="field-label">Name</span>
          <input
            className="field-input" value={name} placeholder="Acme Dental"
            onChange={(e) => {
              const wasAuto = slug === '' || slug === auto(name);
              setName(e.target.value);
              if (wasAuto) setSlug(auto(e.target.value));
            }}
          />
        </label>
        <label className="field">
          <span className="field-label">Slug</span>
          <input className="field-input" value={slug} placeholder="acme-dental"
                 onChange={(e) => setSlug(e.target.value)} />
        </label>
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create'}
        </button>
      </form>
      <p className="panel-note">
        Lowercase letters, digits and hyphens. The slug is part of the org's identity and awkward to
        change later, so pick it deliberately.
      </p>
    </section>
  );
}

// -------------------------------------------------------------- Org detail

export function OrgDetailView({ orgId, onBack }: { orgId: string; onBack: () => void }) {
  const { data, loading, error, reload } = useLoad(() => overview({}), [orgId]);
  const org = data?.orgs.find((o) => o.orgId === orgId);
  const plans = useLoad(() => listPlans({}));

  return (
    <Shell loading={loading} error={error}>
      <button className="btn-back" onClick={onBack}><Ico d={ICON.back} /> All tenants</button>

      {!org ? <div className="empty">That organisation no longer exists.</div> : (
        <>
          <section className="panel">
            <div className="panel-head">
              <div>
                <h3 className="panel-title" style={{ marginBottom: 2 }}>{org.name}</h3>
                <div className="mono dim" style={{ fontSize: 11 }}>{org.slug} · {org.orgId}</div>
              </div>
              <StatusTag status={org.status} />
            </div>

            <div className="stat-row" style={{ marginTop: '.9rem' }}>
              {(['datto', 'sp', 'sc', 'msp', 'unitrends', 'email'] as Source[]).map((s) => (
                <div className="stat" key={s}>
                  <div className="stat-label">{SOURCE_LABEL[s]}</div>
                  <div style={{ marginTop: 6 }}><CountPills c={org.bySource?.[s] ?? ZERO} /></div>
                  <div className="stat-sub">
                    {org.lastSyncOk?.[s] === false
                      ? <span className="tag tag-bad">last sync failed</span>
                      : <span className="dim">last sync ok</span>}
                  </div>
                </div>
              ))}
            </div>

            <div className="row-form" style={{ marginTop: '1rem' }}>
              <label className="field">
                <span className="field-label">Plan</span>
                <select
                  className="field-input" value={org.planId ?? ''}
                  onChange={async (e) => {
                    await setOrgPlan({ orgId, planId: e.target.value || null });
                    await reload();
                  }}
                >
                  <option value="">— none —</option>
                  {(plans.data?.plans ?? []).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </label>
              <button
                className="btn"
                onClick={async () => {
                  await setOrgStatus({ orgId, status: org.status === 'active' ? 'suspended' : 'active' });
                  await reload();
                }}
              >
                {org.status === 'active' ? 'Suspend organisation' : 'Activate organisation'}
              </button>
            </div>
          </section>

          <IntegrationsPanel orgId={orgId} />
          <VendorsPanel orgId={orgId} />
          <Team orgId={orgId} />
        </>
      )}
    </Shell>
  );
}

function Team({ orgId }: { orgId: string }) {
  const { data, loading, error, reload } = useLoad(() => orgTeam({ orgId }), [orgId]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('tech');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true); setErr(null);
    try { await fn(); await reload(); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  }

  const members: Member[] = data?.members ?? [];

  return (
    <section className="panel">
      <h3 className="panel-title">Team</h3>
      {err && <div className="banner banner-error">{err}</div>}
      <Shell loading={loading} error={error}>
        <table className="mini-table">
          <thead>
            <tr><th>Email</th><th>Role</th><th>Status</th><th>2FA</th><th>Last sign-in</th><th /></tr>
          </thead>
          <tbody>
            {members.length === 0 && <tr><td colSpan={6} className="dim">No members yet.</td></tr>}
            {members.map((m) => {
              // The server refuses both of these anyway - the point of
              // disabling the buttons is that the reason is visible BEFORE the
              // click, rather than arriving as an error afterwards.
              const lastOwner = m.role === 'owner'
                && members.filter((o) => o.role === 'owner' && !o.disabled).length <= 1;
              return (
                <tr key={m.uid}>
                  <td className="mono">{m.email ?? m.uid}</td>
                  <td><span className="tag tag-warn">{m.role}</span></td>
                  <td><StateTag state={m.state} /></td>
                  <td><MfaTag on={m.mfaEnrolled} /></td>
                  <td className="mono dim">{ago(m.lastSignIn)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {m.state === 'invited' && (
                      <>
                        <button className="btn-link" disabled={busy}
                                onClick={() => run(() => resendInvite({ orgId, uid: m.uid }))}>
                          resend invite
                        </button>
                        {' · '}
                      </>
                    )}
                    <button
                      className="btn-link" disabled={busy || (!m.disabled && lastOwner)}
                      title={!m.disabled && lastOwner ? 'The only owner cannot be disabled' : ''}
                      onClick={() => run(() => setMemberDisabled({ orgId, uid: m.uid, disabled: !m.disabled }))}
                    >
                      {m.disabled ? 'enable' : 'disable'}
                    </button>
                    {' · '}
                    <button
                      className="btn-link" disabled={busy || lastOwner}
                      title={lastOwner ? 'Make somebody else an owner first' : ''}
                      onClick={() => run(() => removeMember({ orgId, uid: m.uid }))}
                    >
                      remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Shell>

      <form
        className="row-form" style={{ marginTop: '.8rem' }}
        onSubmit={(e) => {
          e.preventDefault();
          if (!email.trim()) return;
          void run(() => setMemberRole({ orgId, email: email.trim(), role }));
          setEmail('');
        }}
      >
        <label className="field">
          <span className="field-label">Add or change member</span>
          <input className="field-input" type="email" value={email} placeholder="person@example.com"
                 onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="field">
          <span className="field-label">Role</span>
          <select className="field-input" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <button className="btn" type="submit" disabled={busy}>Apply</button>
      </form>
      <p className="panel-note">
        The account must already exist in Firebase Auth. owner and admin can manage the
        organisation; manager runs the people; tech and viewer can only see it.
        An organisation always keeps at least one owner - the last one cannot be removed,
        disabled or demoted, because owner is the only role that can promote anybody and a
        tenant with none cannot repair itself.
      </p>
    </section>
  );
}

// --------------------------------------------------------------- Sync health

export function SyncHealthView() {
  const { data, loading, error, reload } = useLoad(() => syncHealth({}));
  const runs: SyncRun[] = data?.runs ?? [];
  const failing = runs.filter((r) => !r.ok);

  return (
    <Shell loading={loading} error={error}>
      {failing.length > 0 && (
        <div className="banner banner-error">
          {failing.length} recent collector run{failing.length === 1 ? '' : 's'} failed.
        </div>
      )}
      <section className="panel">
        <div className="panel-head">
          <h3 className="panel-title">Recent collector runs</h3>
          <button className="btn" onClick={() => void reload()}>Refresh</button>
        </div>
        <table className="mini-table">
          <thead><tr><th>When</th><th>Organisation</th><th>Source</th><th>Result</th><th>Changed</th><th>Took</th></tr></thead>
          <tbody>
            {runs.length === 0 && <tr><td colSpan={6} className="dim">No runs recorded yet.</td></tr>}
            {runs.map((r, i) => (
              <tr key={i}>
                <td className="mono dim">{ago(r.startedAt)}</td>
                <td>{r.orgName}</td>
                <td className="mono">{r.source}</td>
                <td>
                  {r.ok ? <span className="tag tag-ok">ok</span> : (
                    <span title={r.error ?? ''}><span className="tag tag-bad">failed</span>
                      {r.error && <span className="dim" style={{ marginLeft: 6, fontSize: 11 }}>{r.error.slice(0, 60)}</span>}
                    </span>
                  )}
                </td>
                <td className="mono">{r.changed || ''}</td>
                <td className="mono dim">{r.durationMs ? Math.round(r.durationMs / 1000) + 's' : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="panel-note">
          Datto runs every 15 minutes, ShadowControl at :07 :22 :37 :52. A source that stops
          appearing here is a source that stopped running.
        </p>
      </section>
    </Shell>
  );
}

// ------------------------------------------------------------------- Plans

export function PlansView() {
  const { data, loading, error, reload } = useLoad(() => listPlans({}));
  const [draft, setDraft] = useState<Partial<Plan>>({ id: '', name: '', maxTargets: null, maxOrgUsers: null, active: true });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!draft.id) return;
    setBusy(true); setErr(null);
    try {
      await upsertPlan({ ...draft, id: draft.id } as Plan);
      setDraft({ id: '', name: '', maxTargets: null, maxOrgUsers: null, active: true });
      await reload();
    } catch (e2) { setErr(errText(e2)); }
    finally { setBusy(false); }
  }

  return (
    <Shell loading={loading} error={error}>
      {err && <div className="banner banner-error">{err}</div>}
      <section className="panel">
        <h3 className="panel-title">Plans</h3>
        <table className="mini-table">
          <thead><tr><th>Plan</th><th>Max machines</th><th>Max users</th><th>Active</th><th>Includes</th><th /></tr></thead>
          <tbody>
            {(data?.plans ?? []).length === 0 && <tr><td colSpan={6} className="dim">No plans defined.</td></tr>}
            {(data?.plans ?? []).map((p) => (
              <tr key={p.id}>
                <td><div style={{ fontWeight: 600 }}>{p.name}</div><div className="mono dim" style={{ fontSize: 11 }}>{p.id}</div></td>
                <td className="mono">{p.maxTargets ?? '∞'}</td>
                <td className="mono">{p.maxOrgUsers ?? '∞'}</td>
                <td><span className={'tag ' + (p.active ? 'tag-ok' : 'tag-bad')}>{p.active ? 'yes' : 'no'}</span></td>
                <td className="dim" style={{ fontSize: 11 }}>
                  {(p.disabledFeatures ?? []).length === 0
                    ? 'everything'
                    : (data?.optionalFeatures ?? [])
                        .filter((f) => !(p.disabledFeatures ?? []).includes(f.id))
                        .map((f) => f.label).join(', ') || 'nothing'}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn-link" onClick={() => setDraft(p)}>edit</button>
                  {' · '}
                  <button
                    className="btn-link"
                    onClick={async () => {
                      setErr(null);
                      try { await deletePlan({ id: p.id }); await reload(); }
                      catch (e2) { setErr(errText(e2)); }
                    }}
                  >
                    delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="panel-note">
          A plan that is assigned to an organisation cannot be deleted — orphaning a tenant's plan
          is harder to notice than an error here.
        </p>
      </section>

      <section className="panel">
        <h3 className="panel-title">{draft.id ? 'Edit plan' : 'New plan'}</h3>
        <form className="row-form" onSubmit={save}>
          <label className="field">
            <span className="field-label">Id</span>
            <input className="field-input" value={draft.id ?? ''} placeholder="standard"
                   onChange={(e) => setDraft({ ...draft, id: e.target.value })} />
          </label>
          <label className="field">
            <span className="field-label">Name</span>
            <input className="field-input" value={draft.name ?? ''} placeholder="Standard"
                   onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </label>
          <label className="field">
            <span className="field-label">Max machines</span>
            <input className="field-input" type="number" value={draft.maxTargets ?? ''} placeholder="∞"
                   onChange={(e) => setDraft({ ...draft, maxTargets: e.target.value === '' ? null : Number(e.target.value) })} />
          </label>
          <label className="field">
            <span className="field-label">Max users</span>
            <input className="field-input" type="number" value={draft.maxOrgUsers ?? ''} placeholder="∞"
                   onChange={(e) => setDraft({ ...draft, maxOrgUsers: e.target.value === '' ? null : Number(e.target.value) })} />
          </label>
          <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          {draft.id && (
            <button className="btn" type="button"
                    onClick={() => setDraft({ id: '', name: '', maxTargets: null, maxOrgUsers: null, active: true })}>
              Cancel
            </button>
          )}
        </form>

        <div style={{ marginTop: 14 }}>
          <div className="field-label" style={{ marginBottom: 6 }}>Included features</div>
          {(data?.optionalFeatures ?? []).map((f) => {
            const off = (draft.disabledFeatures ?? []).includes(f.id);
            return (
              <label key={f.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
                <input
                  type="checkbox"
                  checked={!off}
                  onChange={(e) => {
                    const set = new Set(draft.disabledFeatures ?? []);
                    if (e.target.checked) set.delete(f.id); else set.add(f.id);
                    setDraft({ ...draft, disabledFeatures: [...set] });
                  }}
                />
                <span>
                  <strong>{f.label}</strong>
                  {off && <span className="dim"> {f.effect}</span>}
                </span>
              </label>
            );
          })}
        </div>

        <p className="panel-note">
          Leave a limit blank for unlimited. A ticked feature is included; unticking it removes
          that feature from every role in every organisation on this plan, including the owner.
        </p>
      </section>
    </Shell>
  );
}

// -------------------------------------------------------- Platform admins

/**
 * Platform administrators.
 *
 * This is now the ONLY people page in the console. The old "Users" view listed
 * every account across every tenant, which was a second and worse way to browse
 * a tenant's team - worse because it showed the membership without the state,
 * the second factor, or any of the actions. Tenant members belong under their
 * tenant, where all of that is available; this page is for platform-level
 * people only.
 *
 * THREE ACTIONS, AND THEY ARE NOT THE SAME THING:
 *   Revoke   takes away platform access and leaves the account alone. The
 *            person keeps whatever tenant memberships they had.
 *   Disable  suspends the FIREBASE USER, so they cannot sign in here or to any
 *            tenant. This is the one for "they have left".
 *   Invite   adds somebody who may not have an account at all - the console
 *            creates it and emails a one-time sign-in link.
 *
 * None of the three can be aimed at yourself. The buttons say so rather than
 * failing after the click, and the server refuses independently, because a
 * hidden button is not a rule.
 */
export function AdminsView({ selfEmail }: { selfEmail: string }) {
  const { data, loading, error, reload } = useLoad(() => listUsers({}));
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [lookup, setLookup] = useState('');
  const [found, setFound] = useState<string | null>(null);
  const [inviteRole, setInviteRole] = useState<PlatformRole>('employee');

  // Ask the server what this caller may do rather than deciding here. Hiding a
  // control the server would allow is merely annoying; showing one it will
  // refuse is a bug report.
  const [cat, setCat] = useState<PlatformRoleCatalog | null>(null);
  useEffect(() => {
    let live = true;
    platformRoleCatalog({})
      .then((c) => { if (live) setCat(c); })
      .catch(() => { /* the page still works read-only */ });
    return () => { live = false; };
  }, []);
  const assignable = cat?.assignable ?? [];
  const mayManage = (cat?.myCapabilities ?? []).includes('admins.manage');

  const admins = (data?.users ?? []).filter((u) => u.platformAdmin);
  const isSelf = (u: PlatformUser) => (u.email ?? '').toLowerCase() === selfEmail.toLowerCase();

  async function run(fn: () => Promise<string>) {
    setBusy(true); setErr(null); setMsg(null);
    try { setMsg(await fn()); await reload(); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  }

  return (
    <Shell loading={loading} error={error}>
      {err && <div className="banner banner-error">{err}</div>}
      {msg && <div className="banner banner-ok">{msg}</div>}

      <section className="panel">
        <h3 className="panel-title">Platform administrators</h3>
        <table className="mini-table">
          <thead>
            <tr><th>Email</th><th>Role</th><th>Status</th><th>2FA</th><th>Last sign-in</th><th /></tr>
          </thead>
          <tbody>
            {admins.length === 0 && <tr><td colSpan={6} className="dim">None.</td></tr>}
            {admins.map((a) => {
              const self = isSelf(a);
              return (
                <tr key={a.uid}>
                  <td className="mono">
                    {a.email ?? a.uid}
                    {self && <span className="dim"> (you)</span>}
                  </td>
                  <td>
                    {/* Changing your own role is refused by the server too: an
                        Owner who makes themselves an Employee can no longer
                        promote anybody, including themselves. */}
                    {assignable.length > 0 && !self ? (
                      <select
                        className="field-input" style={{ padding: '2px 6px', fontSize: 12 }}
                        value={a.platformRole ?? 'owner'} disabled={busy}
                        onChange={(e) => void run(async () => {
                          const role = e.target.value as PlatformRole;
                          await setPlatformRole({ uid: a.uid, role });
                          return `${a.email} is now ${role}.`;
                        })}
                      >
                        {assignable.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    ) : (
                      <span className="tag tag-warn">{a.platformRole ?? 'owner'}</span>
                    )}
                  </td>
                  <td><StateTag state={a.state} /></td>
                  <td><MfaTag on={a.mfaEnrolled} /></td>
                  <td className="mono dim">{ago(a.lastSignIn)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      className="btn-link"
                      disabled={busy || !mayManage || (!a.disabled && self)}
                      title={!a.disabled && self ? 'You cannot disable your own account' : ''}
                      onClick={() => void run(async () => {
                        await setUserDisabled({ uid: a.uid, disabled: !a.disabled });
                        return a.disabled
                          ? `${a.email} can sign in again.`
                          : `${a.email} is disabled and cannot sign in anywhere.`;
                      })}
                    >
                      {a.disabled ? 'enable' : 'disable'}
                    </button>
                    {' · '}
                    <button
                      className="btn-link danger"
                      disabled={busy || self || !mayManage}
                      title={self ? 'You cannot revoke your own platform access' : ''}
                      onClick={() => {
                        if (!confirm(`Remove platform access for ${a.email}? Their tenant memberships are untouched.`)) return;
                        void run(async () => {
                          await setPlatformAdmin({ email: a.email ?? '', grant: false });
                          return `${a.email} is no longer a platform admin.`;
                        });
                      }}
                    >
                      delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="panel-note">
          Platform admins can see and manage every organisation. Grant sparingly — this is the
          keys-to-everything role, which is also why "no 2FA" is called out above rather than left
          as a blank space. Delete removes platform access only; disable suspends the account
          everywhere, including in any tenant they belong to.
        </p>
      </section>

      <section className="panel">
        <h3 className="panel-title">Invite a platform admin</h3>
        <form
          className="row-form"
          onSubmit={(e) => {
            e.preventDefault();
            const addr = email.trim();
            if (!addr) return;
            void run(async () => {
              const r = await invitePlatformAdmin({ email: addr, role: inviteRole });
              setEmail('');
              // "Added but never told" must not read as plain success.
              if (r.created && !r.emailed) return `${addr} was added, but the invitation email failed: ${r.emailError}`;
              if (r.created) return `Invited ${addr}. They will set their own password from the link.`;
              return `${addr} already had an account and is now a platform admin.`;
            });
          }}
        >
          <label className="field">
            <span className="field-label">Email</span>
            <input className="field-input" type="email" value={email} placeholder="person@vtgtx.com"
                   onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">Role</span>
            <select className="field-input" value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as PlatformRole)}>
              {(assignable.length ? assignable : (['employee'] as PlatformRole[]))
                .map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <button className="btn btn-primary" type="submit"
                  disabled={busy || !email.trim() || !mayManage}
                  title={mayManage ? '' : 'Only an Owner can grant platform access'}>
            {busy ? 'Working' : 'Invite'}
          </button>
        </form>
        <p className="panel-note">
          If the address has no account, one is created and sent a one-time sign-in link — no
          password is ever generated or emailed. If it already has an account, it is simply granted
          access and no mail is sent, because an unexpected "here is your way in" message is the
          exact thing people should be taught not to click.
        </p>
      </section>

      <section className="panel">
        <h3 className="panel-title">Look up an account</h3>
        <form
          className="row-form"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!lookup.trim()) return;
            try {
              const r = await findUser({ email: lookup.trim() });
              setFound(r.found
                ? `${r.email} · uid ${r.uid} · created ${r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '?'} · last sign-in ${r.lastSignIn ? new Date(r.lastSignIn).toLocaleString() : 'never'}${r.disabled ? ' · DISABLED' : ''}`
                : `No Firebase Auth account for ${lookup.trim()}.`);
            } catch (err2) { setFound(errText(err2)); }
          }}
        >
          <label className="field">
            <span className="field-label">Email</span>
            <input className="field-input" type="email" value={lookup}
                   onChange={(e) => setLookup(e.target.value)} placeholder="person@example.com" />
          </label>
          <button className="btn" type="submit">Look up</button>
        </form>
        {found && <p className="panel-note mono">{found}</p>}
        <p className="panel-note">
          Answers "does this person have an account at all", which is the question behind most
          "why did that not work" moments when adding somebody to a tenant.
        </p>
      </section>
    </Shell>
  );
}

// ------------------------------------------------------------------- Audit

export function AuditView() {
  const { data, loading, error, reload } = useLoad(() => listAudit({ limit: 200 }));
  const entries: AuditEntry[] = data?.entries ?? [];

  return (
    <Shell loading={loading} error={error}>
      <section className="panel">
        <div className="panel-head">
          <h3 className="panel-title">Audit log</h3>
          <button className="btn" onClick={() => void reload()}>Refresh</button>
        </div>
        <table className="mini-table">
          <thead><tr><th>When</th><th>Who</th><th>Action</th><th>Detail</th></tr></thead>
          <tbody>
            {entries.length === 0 && (
              <tr><td colSpan={4} className="dim">
                Nothing recorded yet. Entries appear as platform changes are made.
              </td></tr>
            )}
            {entries.map((e) => (
              <tr key={e.id}>
                <td className="mono dim">{e.at ? new Date(e.at).toLocaleString() : '--'}</td>
                <td className="mono">{e.actorEmail ?? '--'}</td>
                <td><span className="tag tag-warn">{e.action}</span></td>
                <td className="mono dim" style={{ fontSize: 11 }}>
                  {Object.entries(e.detail ?? {}).map(([k, v]) => `${k}=${String(v)}`).join(' · ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </Shell>
  );
}
